import React, { useMemo, useState } from "react";
import { ChevronDown, BookPlus } from "lucide-react";
import { pairUniverse } from "../mock";
import TradingViewWidget from "./TradingViewWidget";
import PanePnl, { PositionOverlay, usePanePositions } from "./PanePnl";

const INTERVALS = [
  { v: "15", l: "15m" },
  { v: "60", l: "1H" },
  { v: "240", l: "4H" },
  { v: "D", l: "1D" },
  { v: "W", l: "1W" },
];

const STYLES = [
  { v: "1", l: "Candles" },
  { v: "0", l: "Bars" },
  { v: "3", l: "Area" },
  { v: "2", l: "Line" },
  { v: "9", l: "Heikin" },
];

const ChartPane = ({ pane, onChange, onLog, theme = "dark", rich = true }) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const posData = usePanePositions(pane.symbol);
  const style = pane.style || "1";
  const isLight = theme === "light";

  const grouped = useMemo(() => {
    const g = {};
    pairUniverse.forEach((p) => { (g[p.cat] = g[p.cat] || []).push(p); });
    return g;
  }, []);

  const watchlist = useMemo(() => pairUniverse.map((p) => p.tv).slice(0, 14), []);

  const meta = pairUniverse.find((p) => p.tv === pane.symbol);
  const activeLabel = meta?.label || pane.symbol;
  const intervalLabel = INTERVALS.find((i) => i.v === pane.interval)?.l || pane.interval;

  const config = useMemo(() => ({
    autosize: true,
    symbol: pane.symbol,
    interval: pane.interval,
    timezone: "Etc/UTC",
    theme,
    style,
    locale: "en",
    backgroundColor: isLight ? "#ffffff" : "rgba(8, 13, 26, 0.6)",
    gridColor: isLight ? "rgba(42, 46, 57, 0.06)" : "rgba(80, 130, 220, 0.10)",
    toolbar_bg: isLight ? "#f8fafc" : "rgba(8, 13, 26, 0.9)",
    hide_top_toolbar: false,
    hide_side_toolbar: false,
    hide_legend: false,
    allow_symbol_change: true,
    withdateranges: true,
    details: false,
    hotlist: false,
    calendar: false,
    hide_volume: true,
    show_popup_button: true,
    popup_width: "1200",
    popup_height: "720",
    watchlist: undefined,
    study_count_limit: 10,
    save_image: true,
    support_host: "https://www.tradingview.com",
  }), [pane.symbol, pane.interval, style, theme, isLight, rich, watchlist]);

  return (
    <div className="ws-pane flex flex-col h-full rounded-[16px]">
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b border-[color:var(--ws-border)] rounded-t-[16px]">
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
        <div className="relative">
          <button onClick={() => setPickerOpen((o) => !o)} className="ws-btn-ghost px-3 py-1.5 rounded-lg text-[13px] font-semibold inline-flex items-center gap-1.5">
            {activeLabel} <ChevronDown className="w-3.5 h-3.5" />
          </button>
          {pickerOpen && (
            <div className="ws-picker ws-picker-left">
              {["Crypto", "Metals", "Forex", "Indices"].map((cat) => (
                <div key={cat} className="mb-2">
                  <div className="text-[10px] tracking-[0.18em] uppercase text-[color:var(--ws-blue-bright)] mb-1.5">{cat}</div>
                  <div className="grid grid-cols-2 gap-1">
                    {(grouped[cat] || []).map((p) => (
                      <button
                        key={p.id}
                        onClick={() => { onChange({ ...pane, symbol: p.tv }); setPickerOpen(false); }}
                        className={`text-left text-[12px] px-2 py-1 rounded-md transition-colors ${pane.symbol === p.tv ? "bg-[rgba(59,130,246,0.2)] text-white" : "text-[color:var(--ws-muted)] hover:text-white hover:bg-[rgba(59,130,246,0.08)]"}`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <PanePnl data={posData} />
        </div>

        <div className="flex items-center gap-1 flex-wrap">
          {INTERVALS.map((iv) => (
            <button
              key={iv.v}
              onClick={() => onChange({ ...pane, interval: iv.v })}
              className={`px-2 py-1 rounded-md text-[11px] font-semibold transition-colors ${pane.interval === iv.v ? "bg-[rgba(59,130,246,0.22)] text-white" : "text-[color:var(--ws-muted)] hover:text-white"}`}
            >
              {iv.l}
            </button>
          ))}
          <span className="w-px h-4 bg-[color:var(--ws-border)] mx-1" />
          <select
            value={style}
            onChange={(e) => onChange({ ...pane, style: e.target.value })}
            className="bg-[rgba(10,17,33,0.8)] border border-[color:var(--ws-border)] rounded-md text-[11px] text-white px-1.5 py-1 outline-none cursor-pointer"
            title="Chart type"
          >
            {STYLES.map((s) => (
              <option key={s.v} value={s.v}>{s.l}</option>
            ))}
          </select>
        </div>

        <button
          onClick={() => onLog({ symbol: activeLabel, interval: intervalLabel })}
          className="ws-btn-primary px-3 py-1.5 rounded-lg text-[12px] font-semibold inline-flex items-center gap-1.5 shrink-0"
          title="Log this trade to your journal"
        >
          <BookPlus className="w-3.5 h-3.5" /> Log Trade
        </button>
      </div>

      <div className="ws-tv-chart flex-1 min-h-0 overflow-hidden rounded-b-[16px] relative">
        <TradingViewWidget
          key={`${pane.symbol}-${pane.interval}-${style}-${theme}`}
          scriptSrc="https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js"
          config={config}
          style={{ height: "100%", width: "100%" }}
        />
        <PositionOverlay data={posData} />
      </div>
    </div>
  );
};

export default ChartPane;
